import { useCallback, useMemo } from 'react';
import type {
  EligibilityCheckResponse,
  OrganizedCoverages,
  StatusLight,
} from '@features/patient-coverages';
import {
  API_STATUS_TO_LIGHT,
  useGetEncounterEligibilityQuery,
  useGetPatientEligibilityQuery,
  useOrganizedPatientCoverages,
  useRunEligibilityCheckMutation,
  useRunPatientEligibilityCheckMutation,
} from '@features/patient-coverages';
import { FeatureGates, useFeatureGate } from '@services/statsig';

export type BadgeMode = 'hidden' | 'no_coverage' | 'unchecked' | 'checked';

interface UseEligibilityBadgeStateOptions {
  patientId?: string;
  encounterId?: string;
}

export interface UseEligibilityBadgeStateResult {
  mode: BadgeMode;
  eligibilityData: EligibilityCheckResponse | undefined;
  status: StatusLight | undefined;
  organized: OrganizedCoverages;
  isLoading: boolean;
  runCheck: () => void;
  isRunning: boolean;
  canRunCheck: boolean;
}

export function useEligibilityBadgeState({
  patientId,
  encounterId,
}: UseEligibilityBadgeStateOptions): UseEligibilityBadgeStateResult {
  const isEnabled = useFeatureGate(FeatureGates.ELIGIBILITY_CHECKS);

  const { organized, isLoading: isCoveragesLoading } =
    useOrganizedPatientCoverages(patientId || '');

  const hasCoverages = !!(organized.primary || organized.secondary || organized.tertiary);

  // Encounter-scoped query: used when encounterId is available
  const {
    data: encounterEligibilityData,
    isLoading: isEncounterEligibilityLoading,
  } = useGetEncounterEligibilityQuery(
    { encounterId: encounterId ?? '' },
    { skip: !isEnabled || !encounterId },
  );

  // Patient-scoped query: fallback when no encounterId
  const {
    data: patientEligibilityData,
    isLoading: isPatientEligibilityLoading,
  } = useGetPatientEligibilityQuery(
    { patientId: patientId ?? '' },
    { skip: !isEnabled || !!encounterId || !patientId },
  );

  // Prefer encounter-scoped, fall back to patient-scoped
  const eligibilityData = encounterEligibilityData ?? patientEligibilityData;

  // Encounter-scoped mutation
  const [triggerEncounterCheck, { isLoading: isEncounterRunning }] =
    useRunEligibilityCheckMutation();

  // Patient-scoped mutation
  const [triggerPatientCheck, { isLoading: isPatientRunning }] =
    useRunPatientEligibilityCheckMutation();

  const runCheck = useCallback(() => {
    if (encounterId) {
      triggerEncounterCheck({ encounterId });
    } else if (patientId) {
      triggerPatientCheck({ patientId });
    }
  }, [encounterId, patientId, triggerEncounterCheck, triggerPatientCheck]);

  const mode = useMemo<BadgeMode>(() => {
    if (!isEnabled) return 'hidden';
    if (eligibilityData) return 'checked';
    if (hasCoverages) return 'unchecked';
    return 'no_coverage';
  }, [isEnabled, eligibilityData, hasCoverages]);

  const status = useMemo<StatusLight | undefined>(() => {
    if (mode !== 'checked' || !eligibilityData) return undefined;
    return API_STATUS_TO_LIGHT[eligibilityData.status] ?? 'YELLOW';
  }, [mode, eligibilityData]);

  const isLoading =
    (isEnabled && !!patientId && isCoveragesLoading) ||
    (isEnabled && !!encounterId && isEncounterEligibilityLoading) ||
    (isEnabled && !encounterId && !!patientId && isPatientEligibilityLoading);

  const isRunning = isEncounterRunning || isPatientRunning;

  return {
    mode,
    eligibilityData,
    status,
    organized,
    isLoading,
    runCheck,
    isRunning,
    canRunCheck: !!encounterId || !!patientId,
  };
}
